import {
  Box,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
  Autocomplete,
  Popper,
} from "@mui/material";
import { X } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { fetchGetDocument, useListFeaturedDocuments } from "services/documents";
import { ListFeaturedDocumentsRequest } from "protogen/documents_service_pb";
import { Document } from "protogen/documents_pb";
import FlexBox from "components/common/FlexBox";
import LinkRouter from "components/navigation/LinkRouter";

export type LoadDocumentMetadata = {
  id: string;
  title: string;
  description: string;
  image: string;
  header: string;
};

export const LoadDocument = ({
  onDocumentSelect,
  disabled,
  initialDocumentId,
  listOptions,
  topics,
}: {
  onDocumentSelect: (document: LoadDocumentMetadata | null) => void;
  disabled: boolean;
  initialDocumentId?: string;
  listOptions?: boolean;
  topics?: string[];
}) => {
  const [input, setInput] = useState(initialDocumentId || "");
  const [isLoading, setIsLoading] = useState(false);
  const [document, setDocument] = useState<LoadDocumentMetadata | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [documentsLoaded, setDocumentsLoaded] = useState(false);
  const hasFocusedRef = useRef(false);

  const { request: listDocumentsRequest } = useListFeaturedDocuments();

  const parseDocumentId = (input: string): string | null => {
    if (!input.trim()) return null;
    // if it doesn't match a UUID or URL, return null
    const isUuid = input.match(/^[A-Za-z0-9_-]+={0,2}$/);
    const isUrl = input.match(/\/documents\//);
    if (!isUuid && !isUrl) {
      return null;
    }
    if (isUuid) {
      return input.trim();
    }

    try {
      // Parse as URL to extract document ID
      const url = new URL(input, window.location.origin);
      const decodedPath = decodeURIComponent(url.pathname);
      const docsMatch = decodedPath.match(
        /\/documents\/([A-Za-z0-9_-]+={0,2})(?:[?#].*)?$/,
      );

      return docsMatch ? docsMatch[1] : null;
    } catch {
      // If URL parsing fails, treat as direct ID
      return input.trim();
    }
  };

  const fetchDocument = async (documentId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetchGetDocument(documentId);
      if (!response || !response.document) {
        setError("Document not found");
        setDocument(null);
        onDocumentSelect(null);
        return;
      }

      const owner = response.document?.owner?.displayName || "";
      const docMetadata: LoadDocumentMetadata = {
        id: documentId,
        title: response.document?.title || "Untitled Document",
        description: owner ? `By ${owner}` : "",
        image: "/assets/images/document-placeholder.png",
        header: "Document template",
      };

      setDocument(docMetadata);
      onDocumentSelect(docMetadata);
    } catch (err) {
      console.error("Error fetching document:", err);
      setError("Failed to fetch document");
      setDocument(null);
      onDocumentSelect(null);
    } finally {
      setIsLoading(false);
    }
  };

  const convertDocumentToMetadata = (doc: Document): LoadDocumentMetadata => {
    const owner = doc.owner?.displayName || "";
    return {
      id: doc.uuid || "",
      title: doc.title || "Untitled Document",
      description: owner ? `By ${owner}` : "",
      image: "/assets/images/document-placeholder.png",
      header: "Output document",
    };
  };

  const loadDocuments = async () => {
    if (!listOptions || documentsLoaded) return;

    try {
      const response = await listDocumentsRequest(
        new ListFeaturedDocumentsRequest({
          anyTopics: topics,
        }),
      );
      setDocuments(response?.featuredDocuments.map((d) => d.document!) || []);
      setDocumentsLoaded(true);
    } catch (error) {
      console.error("Error loading documents:", error);
    }
  };

  // Load initial document if initialDocumentId is provided
  useEffect(() => {
    if (initialDocumentId && !document && !isLoading) {
      const documentId = parseDocumentId(initialDocumentId);
      if (documentId) {
        fetchDocument(documentId);
      }
    }
  }, []);

  const handleInputChange = (value: string) => {
    setInput(value);
    setError(null);

    const documentId = parseDocumentId(value);
    if (documentId) {
      fetchDocument(documentId);
    } else {
      setDocument(null);
      onDocumentSelect(null);
    }
  };

  const handleClear = () => {
    setInput("");
    setDocument(null);
    setError(null);
    onDocumentSelect(null);
  };

  const handleFocus = () => {
    if (listOptions && !hasFocusedRef.current) {
      hasFocusedRef.current = true;
      loadDocuments();
    }
  };

  const handleDocumentSelect = (doc: Document | null) => {
    if (doc) {
      const metadata = convertDocumentToMetadata(doc);
      setDocument(metadata);
      setInput(doc.uuid || "");
      onDocumentSelect(metadata);
    } else {
      handleClear();
    }
  };

  const filteredDocuments = documents.filter(
    (doc) =>
      doc.title?.toLowerCase().includes(input.toLowerCase()) ||
      doc.owner?.displayName?.toLowerCase().includes(input.toLowerCase()),
  );

  if (listOptions && !document) {
    return (
      <Box
        sx={{ display: "flex", flexDirection: "column", gap: "12px", flex: 1 }}
      >
        <Autocomplete
          options={filteredDocuments}
          getOptionLabel={(option) => option.title || "Untitled Document"}
          filterOptions={(options) => options} // We handle filtering ourselves
          value={null}
          onChange={(_, value) => handleDocumentSelect(value)}
          onInputChange={(_, value) => handleInputChange(value)}
          inputValue={input}
          loading={isLoading}
          disabled={disabled}
          onFocus={handleFocus}
          sx={{ "& > div": { maxWidth: "none" } }}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              label="Document template"
              placeholder="Search or paste document URL"
              error={!!error}
              helperText={error}
              sx={{
                maxWidth: "300px",
              }}
              InputProps={{
                ...params.InputProps,
                sx: {
                  paddingTop: "0 !important",
                  paddingBottom: "0 !important",
                },
                endAdornment: (
                  <>
                    {isLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <Box
              component="li"
              {...props}
              key={option.uuid || option.ref || `doc-${option.title}`}
              sx={{
                padding: "12px !important",
                borderBottom: "1px solid #f0f0f0",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", width: "100%" }}
              >
                <Box
                  component="img"
                  src="/assets/images/document-placeholder.png"
                  alt={option.title || "Document"}
                  sx={{
                    width: "40px",
                    height: "40px",
                    flexShrink: 0,
                    objectFit: "cover",
                    marginRight: "12px",
                    borderRadius: "4px",
                  }}
                />
                <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
                  <Typography
                    variant="bodyHeavy"
                    color="#198282"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontSize: "12px",
                      marginBottom: "2px",
                    }}
                  >
                    Document
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontSize: "14px",
                      fontWeight: 600,
                      marginBottom: "2px",
                    }}
                  >
                    {option.title || "Untitled Document"}
                  </Typography>
                  {option.owner?.displayName && (
                    <Typography
                      variant="bodySmallHeavy"
                      color="text.secondary"
                      sx={{
                        fontWeight: "600",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontSize: "12px",
                      }}
                    >
                      By {option.owner.displayName}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}
          noOptionsText={
            input ? "No documents found" : "Start typing to search documents"
          }
          PopperComponent={(props) => (
            <Popper {...props} placement="bottom-start" />
          )}
        />
      </Box>
    );
  }

  return (
    <FlexBox direction="column">
      {!document && (
        <TextField
          fullWidth
          label="Output Document (ID or URL)"
          value={input}
          onChange={(e) => handleInputChange(e.target.value)}
          disabled={disabled}
          error={!!error}
          helperText={error}
          placeholder="Enter document ID or URL..."
          sx={{ maxWidth: "400px" }}
        />
      )}
      {(isLoading || document) && (
        <Box
          sx={{
            borderRadius: "12px",
            border: "1px solid #D4D4D4",
            background: "#FFF",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            overflow: "hidden",
            paddingRight: "12px",
            height: "92px",
            maxWidth: "500px",
          }}
        >
          {isLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
              flexGrow={1}
            >
              <CircularProgress size={24} />
            </Box>
          ) : document ? (
            <>
              <Box
                component="img"
                src={document.image}
                alt={document.title}
                sx={{
                  flexShrink: 0,
                  objectFit: "cover",
                  maxWidth: "25% !important",
                  height: "100%",
                }}
              />
              <Box
                display="flex"
                flexDirection="column"
                sx={{
                  flexGrow: 1,
                  overflow: "hidden",
                  flexShrink: 1,
                  marginLeft: "16px",
                  marginRight: "4px",
                  gap: "2px",
                }}
              >
                <LinkRouter
                  to={`/documents/${document.id}`}
                  targetNew
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                  }}
                >
                  <Typography
                    variant="bodyHeavy"
                    color="#198282"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontSize: "14px",
                    }}
                  >
                    {document.header}
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {document.title}
                  </Typography>
                  {document.description && (
                    <Typography
                      variant="bodySmallHeavy"
                      color="text.secondary"
                      sx={{
                        fontWeight: "600",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {document.description}
                    </Typography>
                  )}
                </LinkRouter>
              </Box>
              <IconButton
                onClick={handleClear}
                disabled={disabled}
                sx={{ padding: "4px" }}
              >
                <X size={20} color="#8E9598" />
              </IconButton>
            </>
          ) : null}
        </Box>
      )}
    </FlexBox>
  );
};
